import { Injectable } from '@angular/core';
import { TrpcService } from './trpc.service';

/**
 * Reads backend-controlled feature flags (sourced from the `global_setting`
 * table via the `featureFlags.getAll` tRPC endpoint) so the route guard and
 * menu can decide what to show.
 *
 * Distinct from `FeatureFlagService`, which is per-browser localStorage. This
 * service is the single source of truth for org-wide, server-controlled flags.
 *
 * The whole flag map is fetched once and memoized for the session (a page
 * reload picks up a change). Reads fail CLOSED — if the flags can't be
 * fetched, features are treated as disabled so a backend hiccup hides rather
 * than breaks the UI. A failed read is not cached, so the next call retries.
 */
@Injectable({ providedIn: 'root' })
export class FeatureConfigService {
  private flags?: Promise<Record<string, boolean>>;

  constructor(private trpc: TrpcService) {}

  /** Fetches (once) the full map of server-driven feature flags. */
  getFlags(): Promise<Record<string, boolean>> {
    if (!this.flags) {
      const pending = this.trpc.featureFlags.getAll
        .query()
        .then((res: Record<string, boolean>) => res ?? {})
        .catch(() => {
          this.flags = undefined; // allow a retry on the next call
          return {} as Record<string, boolean>;
        });
      this.flags = pending;
      return pending;
    }
    return this.flags;
  }

  /** True only when the named feature flag is enabled on the backend. */
  async isEnabled(key: string): Promise<boolean> {
    return (await this.getFlags())[key] === true;
  }

  isRateIncreasesEnabled(): Promise<boolean> {
    return this.isEnabled('rateIncreases');
  }

  clearCache(): void {
    this.flags = undefined;
  }
}
